/**
 * Document List Component for Re:Nav-96
 *
 * Displays a list of documents for a group.
 * Supports document viewing, downloading, and deletion.
 *
 * Features:
 * - Document list with file info and timestamps
 * - Download functionality
 * - Delete option for uploaders/admins
 * - Loading and empty states
 * - Accessible with keyboard support
 *
 * @example
 * ```tsx
 * <DocumentList
 *   groupId="group-123"
 *   documents={documents}
 *   isLoading={false}
 *   onDocumentDeleted={handleDelete}
 * />
 * ```
 */

import React, { useState, useCallback } from 'react';
import { documentsApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import './DocumentList.css';

/**
 * Document entity
 */
export interface Document {
  id: string;
  filename: string;
  description?: string;
  uploaderId: string;
  uploaderName?: string;
  mimeType?: string;
  fileSize?: number | string;
  createdAt: string;
}

/**
 * Props for DocumentList component
 */
interface DocumentListProps {
  /** ID of the group */
  groupId: string;
  /** List of documents to display */
  documents: Document[];
  /** Whether documents are loading */
  isLoading: boolean;
  /** Error message if any */
  error?: string | null;
  /** Callback when document is deleted */
  onDocumentDeleted?: (documentId: string) => void;
  /** Optional CSS class */
  className?: string;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes?: number | string): string {
  // SQL bigint surfaces as a string in JSON; coerce defensively.
  const n = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (!n || Number.isNaN(n)) return 'Unknown size';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = n;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(unitIndex > 0 ? 1 : 0)} ${units[unitIndex]}`;
}

/**
 * Format relative time for display
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: diffDays > 365 ? 'numeric' : undefined,
  });
}

/**
 * Get file icon based on mime type
 */
function getFileIcon(mimeType?: string): React.ReactElement {
  if (!mimeType) {
    return (
      <svg className="document-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }

  if (mimeType.startsWith('image/')) {
    return (
      <svg className="document-icon document-icon-image" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }

  if (mimeType === 'application/pdf') {
    return (
      <svg className="document-icon document-icon-pdf" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-6 4h4" />
      </svg>
    );
  }

  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('.sheet')) {
    return (
      <svg className="document-icon document-icon-excel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
      </svg>
    );
  }

  if (mimeType.includes('document') || mimeType.includes('word') || mimeType.includes('msword')) {
    return (
      <svg className="document-icon document-icon-word" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }

  return (
    <svg className="document-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

/**
 * Loading skeleton for documents
 */
function DocumentsLoadingSkeleton() {
  return (
    <div className="documents-loading">
      {[1, 2, 3].map((i) => (
        <div key={i} className="document-skeleton">
          <div className="document-skeleton-icon" />
          <div className="document-skeleton-content">
            <div className="document-skeleton-name" />
            <div className="document-skeleton-meta" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Empty state when no documents
 */
function DocumentsEmptyState() {
  return (
    <div className="documents-empty">
      <svg
        className="w-12 h-12 text-white/20 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
      <h3 className="text-white/60 text-base font-medium mb-1">
        No documents yet
      </h3>
      <p className="text-white/40 text-sm text-center">
        Upload a document to share with the group.
      </p>
    </div>
  );
}

/**
 * Individual document item
 */
interface DocumentItemProps {
  document: Document;
  groupId: string;
  canDelete: boolean;
  onDelete: () => void;
  isDeleting: boolean;
}

function DocumentItem({ document, groupId, canDelete, onDelete, isDeleting }: DocumentItemProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /**
   * Handle document download
   */
  const handleDownload = useCallback(async () => {
    try {
      setIsDownloading(true);
      setDownloadError(null);

      const blob = await documentsApi.download(groupId, document.id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError('Failed to download');
    } finally {
      setIsDownloading(false);
    }
  }, [groupId, document.id, document.filename]);

  return (
    <div className={`document-item ${isDeleting ? 'document-item-deleting' : ''}`}>
      <div className="document-icon-wrapper">
        {getFileIcon(document.mimeType)}
      </div>

      <div className="document-info">
        <div className="document-name" title={document.filename}>
          {document.filename}
        </div>
        <div className="document-meta">
          <span className="document-size">{formatFileSize(document.fileSize)}</span>
          <span className="document-separator">-</span>
          <span className="document-time">{formatRelativeTime(document.createdAt)}</span>
          {document.uploaderName && (
            <>
              <span className="document-separator">-</span>
              <span className="document-uploader">{document.uploaderName}</span>
            </>
          )}
        </div>
        {document.description && (
          <div className="document-description">{document.description}</div>
        )}
        {downloadError && (
          <div className="document-error">{downloadError}</div>
        )}
      </div>

      <div className="document-actions">
        {/* Download button */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={isDownloading || isDeleting}
          className="document-action-btn document-download-btn"
          title="Download"
          aria-label={`Download ${document.filename}`}
        >
          {isDownloading ? (
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          )}
        </button>

        {/* Delete button */}
        {canDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            className="document-action-btn document-delete-btn"
            title="Delete"
            aria-label={`Delete ${document.filename}`}
          >
            {isDeleting ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * DocumentList Component
 *
 * Displays a list of documents with download and delete functionality.
 *
 * @param props - Component props
 * @returns The document list component
 */
export function DocumentList({
  groupId,
  documents,
  isLoading,
  error,
  onDocumentDeleted,
  className = '',
}: DocumentListProps) {
  const { user } = useAuth();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /**
   * Handle document deletion
   */
  const handleDelete = useCallback(async (documentId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setDeletingId(documentId);
      setDeleteError(null);
      await documentsApi.delete(groupId, documentId);
      onDocumentDeleted?.(documentId);
    } catch (err) {
      setDeleteError('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  }, [groupId, onDocumentDeleted]);

  /**
   * Check if user can delete a document
   */
  const canDeleteDocument = useCallback((doc: Document) => {
    if (!user) return false;
    // User can delete their own uploads or if they're an admin
    return doc.uploaderId === user.id ||
           user.role === 'super_admin' ||
           user.role === 'group_admin';
  }, [user]);

  return (
    <div className={`document-list ${className}`}>
      {/* Error message */}
      {(error || deleteError) && (
        <div className="document-list-error">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error || deleteError}</span>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <DocumentsLoadingSkeleton />
      ) : documents.length === 0 ? (
        <DocumentsEmptyState />
      ) : (
        <div className="documents-grid">
          {documents.map((doc) => (
            <DocumentItem
              key={doc.id}
              document={doc}
              groupId={groupId}
              canDelete={canDeleteDocument(doc)}
              onDelete={() => handleDelete(doc.id)}
              isDeleting={deletingId === doc.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DocumentList;
